
import { useState } from 'react';

/* -------------------- Primary Components -------------------- */

/** Code block with copy‑to‑clipboard support */
export function CodeBlock({
  code,
  language = 'json',
}: {
  code: string;
  language?: string;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  return (
    <div className="relative group my-4">
      <div className="flex items-center justify-between bg-slate-800 rounded-t-lg px-4 py-2 border border-slate-700 border-b-0">
        <span className="text-xs text-slate-400 font-mono">{language}</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-400 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
          {copied ? '已复制' : '复制'}
        </button>
      </div>
      <pre className="bg-slate-950 border border-slate-700 rounded-b-lg p-4 overflow-x-auto">
        <code className="text-sm text-slate-300 font-mono leading-relaxed">{code}</code>
      </pre>
    </div>
  );
}

/** Badge component with colour variants */
export function Badge({
  children,
  color = 'cyan',
}: {
  children: React.ReactNode;
  color?: string;
}) {
  const colors: Record<string, string> = {
    cyan: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30',
    amber: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    rose: 'bg-rose-500/10 text-rose-400 border-rose-500/30',
    slate: 'bg-slate-500/10 text-slate-400 border-slate-500/30',
    teal: 'bg-teal-500/10 text-teal-400 border-teal-500/30',
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
        colors[color] || colors.cyan
      }`}
    >
      {children}
    </span>
  );
}

/** Informational box (info / warning / tip) */
export function InfoBox({
  type = 'info',
  children,
}: {
  type?: 'info' | 'warning' | 'tip';
  children: React.ReactNode;
}) {
  const styles = {
    info: {
      bg: 'bg-cyan-500/5 border-cyan-500/20',
      icon: 'ri-information-line',
      iconColor: 'text-cyan-400',
    },
    warning: {
      bg: 'bg-amber-500/5 border-amber-500/20',
      icon: 'ri-alert-line',
      iconColor: 'text-amber-400',
    },
    tip: {
      bg: 'bg-emerald-500/5 border-emerald-500/20',
      icon: 'ri-lightbulb-line',
      iconColor: 'text-emerald-400',
    },
  };

  const s = styles[type];

  return (
    <div className={`${s.bg} border rounded-lg p-4 my-4`}>
      <div className="flex items-start gap-3">
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
          <i className={`${s.icon} ${s.iconColor} text-lg`}></i>
        </div>
        <div className="text-sm text-slate-300 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

/** Section title with an anchor id */
export function SectionTitle({
  id,
  children,
}: {
  id: string;
  children: React.ReactNode;
}) {
  return (
    <h2
      id={id}
      className="text-xl font-bold text-white mt-10 mb-4 flex items-center gap-2 scroll-mt-24"
    >
      <span className="w-1 h-5 bg-cyan-400 rounded-full inline-block"></span>
      {children}
    </h2>
  );
}

/** Sub‑title component */
export function SubTitle({
  id,
  children,
}: {
  id?: string;
  children: React.ReactNode;
}) {
  return (
    <h3
      id={id}
      className="text-base font-semibold text-slate-200 mt-6 mb-3 scroll-mt-24"
    >
      {children}
    </h3>
  );
}

/** Table for displaying configuration rows */
export function ConfigTable({
  rows,
}: {
  rows: {
    key: string;
    type: string;
    desc: string;
    default?: string;
  }[];
}) {
  return (
    <div className="overflow-x-auto my-4">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-slate-700">
            <th className="text-left py-2.5 px-3 text-slate-400 font-medium w-1/4">
              参数
            </th>
            <th className="text-left py-2.5 px-3 text-slate-400 font-medium w-1/6">
              类型
            </th>
            <th className="text-left py-2.5 px-3 text-slate-400 font-medium">
              说明
            </th>
            <th className="text-left py-2.5 px-3 text-slate-400 font-medium w-1/5">
              默认值
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className="border-b border-slate-800/60 hover:bg-slate-800/30 transition-colors"
            >
              <td className="py-2.5 px-3">
                <code className="text-cyan-400 font-mono text-xs">{row.key}</code>
              </td>
              <td className="py-2.5 px-3">
                <code className="text-amber-400 font-mono text-xs">{row.type}</code>
              </td>
              <td className="py-2.5 px-3 text-slate-300">{row.desc}</td>
              <td className="py-2.5 px-3">
                {row.default ? (
                  <code className="text-slate-400 font-mono text-xs">
                    {row.default}
                  </code>
                ) : (
                  <span className="text-slate-600">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Inline parameter tag */
export function Param({ name }: { name: string }) {
  return (
    <code className="text-cyan-400 bg-slate-800/60 px-1.5 py-0.5 rounded text-xs font-mono">
      {name}
    </code>
  );
}

/* -----------------------------------------------------------------
   The following duplicate component definitions have been removed
   to resolve "multiple exports with the same name" errors:
   - SectionTitle (arrow‑function version)
   - SubTitle (arrow‑function version)
   - InfoBox (enhanced version)
   - CodeBlock (simplified version)
   -----------------------------------------------------------------
   If you need the alternate styles, consider renaming them (e.g.
   `SectionTitleAlt`, `InfoBoxExtended`, etc.) and exporting those
   names instead of overwriting the originals.
----------------------------------------------------------------- */
